import { create } from 'zustand';
import { getState, useState } from '@/framework';
import type { BaseCells, IExportState, StateType } from '@/version-types';

export type CellInfo = { id: string; hIndex: number; vIndex: number };

export type CellsSelectedInfo = {
    rect: Record<'startHIndex' | 'startVIndex' | 'endHIndex' | 'endVIndex', number>;
    cellInfoList: CellInfo[];
};

export type RowOrColSelectedRectInfo = {
    direction: 'horizontal' | 'vertical';
    index: number;
};

interface TableLayerActiveStates {
    /** 单元格被单选中, 或正在编辑单元格内容的状态 */
    activeCell: CellInfo | null;
    /** 是否正在选择cells(拖拽中) */
    cellsIsSelecting: boolean;
    hasEditingText: boolean;
    lineIsDragging: boolean;
    /**
     * 多选(/单选)单元格的相关状态: 框选的矩形信息, 多选中的cells的信息等
     * cellsSelectedInfo与rowOrColSelectedRectInfo是互斥的, 不能同时存在. 两种状态并不一致, 因为选中单行/列, 有可能选中合并单元格的一半
     */
    cellsSelectedInfo: CellsSelectedInfo | null;
    /**
     * 选中单行/列的相关状态
     * cellsSelectedInfo与rowOrColSelectedRectInfo是互斥的, 不能同时存在. 两种状态并不一致, 因为选中单行/列, 有可能选中合并单元格的一半
     */
    rowOrColSelectedRectInfo: RowOrColSelectedRectInfo | null;
    /** 保存编辑前的baseCells, 编辑时需要可还原至编辑前的布局 */
    baseCellsBeforeEditing: BaseCells | null;
}

type States = TableLayerActiveStates;

type Actions = {
    initState: () => void;
};

type Store = States & Actions;

const initialState: States = {
    activeCell: null,
    hasEditingText: false,
    cellsIsSelecting: false,
    lineIsDragging: false,
    cellsSelectedInfo: null,
    rowOrColSelectedRectInfo: null,
    baseCellsBeforeEditing: null,
};

const createState = () => {
    return create<Store>((set) => ({
        ...initialState,
        initState: () => set({ ...initialState }),
    }));
};

const name = 'table-edit-state';

type ModuleType = StateType<Store, typeof name>;

export const useTableEditState = () => {
    return useState<ModuleType>()[name];
};

export const getTableEditState = (frameworkIdentity: string) => {
    const state = getState<ModuleType>(frameworkIdentity)[name].getState();
    const { setState } = getState<ModuleType>(frameworkIdentity)[name];
    return { ...state, setState };
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
